import React, { useState, useEffect } from 'react';
import styles from './Calendar.module.scss';
import IconsColor from '../../util/iconsColor';

interface CalendarProps {
  selectedDate: string; // YYYY-MM-DD format
  onChange: (date: string) => void;
  minDate?: string;
  maxDate?: string;
  onClose?: () => void;
}

type ViewMode = 'calendar' | 'month' | 'year';

const Calendar: React.FC<CalendarProps> = ({
  selectedDate,
  onChange,
  minDate,
  maxDate,
  onClose,
}) => {
  const [viewMode, setViewMode] = useState<ViewMode>('calendar');
  const [currentMonth, setCurrentMonth] = useState(() => {
    if (selectedDate) {
      const [year, month] = selectedDate.split('-');
      return new Date(parseInt(year), parseInt(month) - 1, 1);
    }
    // If no selectedDate but minDate exists, use minDate
    if (minDate) {
      const [year, month] = minDate.split('-');
      return new Date(parseInt(year), parseInt(month) - 1, 1);
    }
    return new Date();
  });
  const [yearRange, setYearRange] = useState(() => {
    const currentYear = currentMonth.getFullYear();
    const startYear = Math.floor(currentYear / 10) * 10;
    return { start: startYear, end: startYear + 11 };
  });

  // Update currentMonth when selectedDate or minDate changes
  useEffect(() => {
    if (selectedDate) {
      const [year, month] = selectedDate.split('-');
      setCurrentMonth(new Date(parseInt(year), parseInt(month) - 1, 1));
    } else if (minDate) {
      // If no selectedDate but minDate exists, show minDate month
      const [year, month] = minDate.split('-');
      setCurrentMonth(new Date(parseInt(year), parseInt(month) - 1, 1));
    }
  }, [selectedDate, minDate]);

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const monthNamesShort = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ];

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Update year range when current month changes
  useEffect(() => {
    const currentYear = currentMonth.getFullYear();
    const startYear = Math.floor(currentYear / 10) * 10;
    setYearRange({ start: startYear, end: startYear + 11 });
  }, [currentMonth]);

  // Get days in month
  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days: (number | null)[] = [];

    // Add empty cells for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }

    return days;
  };

  // Check if date is disabled
  const isDateDisabled = (year: number, month: number, day: number): boolean => {
    const dateString = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    
    if (minDate && dateString < minDate) return true;
    if (maxDate && dateString > maxDate) return true;
    
    return false;
  };

  // Check if date is selected
  const isDateSelected = (year: number, month: number, day: number): boolean => {
    if (!selectedDate) return false;
    const dateString = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return dateString === selectedDate;
  };

  // Check if date is today
  const isToday = (year: number, month: number, day: number): boolean => {
    const today = new Date();
    return (
      today.getFullYear() === year &&
      today.getMonth() === month &&
      today.getDate() === day
    );
  };

  // Handle day click
  const handleDayClick = (day: number) => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();

    if (isDateDisabled(year, month, day)) return;

    const dateString = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    onChange(dateString);
  };

  // Navigate months
  const previousMonth = () => {
    if (viewMode === 'year') {
      setYearRange({ start: yearRange.start - 12, end: yearRange.end - 12 });
    } else {
      setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
    }
  };

  const nextMonth = () => {
    if (viewMode === 'year') {
      setYearRange({ start: yearRange.start + 12, end: yearRange.end + 12 });
    } else {
      setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
    }
  };

  // Handle month selection
  const handleMonthSelect = (monthIndex: number) => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), monthIndex, 1));
    setViewMode('calendar');
  };

  // Handle year selection
  const handleYearSelect = (year: number) => {
    setCurrentMonth(new Date(year, currentMonth.getMonth(), 1));
    setViewMode('calendar');
  };

  // Check if year is disabled
  const isYearDisabled = (year: number): boolean => {
    if (minDate) {
      const [minYear] = minDate.split('-');
      if (year < parseInt(minYear)) return true;
    }
    if (maxDate) {
      const [maxYear] = maxDate.split('-');
      if (year > parseInt(maxYear)) return true;
    }
    return false;
  };

  // Check if month is disabled
  const isMonthDisabled = (year: number, monthIndex: number): boolean => {
    const dateString = `${year}-${String(monthIndex + 1).padStart(2, '0')}-01`;
    if (minDate && dateString < minDate) {
      const lastDay = new Date(year, monthIndex + 1, 0).getDate();
      const lastDayString = `${year}-${String(monthIndex + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
      return lastDayString < minDate;
    }
    if (maxDate && dateString > maxDate) {
      return true;
    }
    return false;
  };

  // Handle month/year header click
  const handleHeaderClick = () => {
    if (viewMode === 'calendar') {
      setViewMode('month');
    } else if (viewMode === 'month') {
      setViewMode('year');
    }
  };

  const days = getDaysInMonth(currentMonth);
  const currentYear = currentMonth.getFullYear();
  const currentMonthIndex = currentMonth.getMonth();

  // Render month picker
  const renderMonthPicker = () => {
    return (
      <div className={styles.monthPicker}>
        {monthNamesShort.map((month, index) => {
          const disabled = isMonthDisabled(currentYear, index);
          const selected = selectedDate && 
            parseInt(selectedDate.split('-')[0]) === currentYear &&
            parseInt(selectedDate.split('-')[1]) === index + 1;

          return (
            <button
              key={month}
              type="button"
              className={`${styles.monthYearItem} ${selected ? styles.selected : ''} ${
                disabled ? styles.disabled : ''
              }`}
              onClick={() => !disabled && handleMonthSelect(index)}
              disabled={disabled}
            >
              {month}
            </button>
          );
        })}
      </div>
    );
  };

  // Render year picker
  const renderYearPicker = () => {
    const years: number[] = [];
    for (let year = yearRange.start; year <= yearRange.end; year++) {
      years.push(year);
    }

    return (
      <div className={styles.yearPicker}>
        {years.map((year) => {
          const disabled = isYearDisabled(year);
          const selected = selectedDate && parseInt(selectedDate.split('-')[0]) === year;
          const isCurrentYear = new Date().getFullYear() === year;

          return (
            <button
              key={year}
              type="button"
              className={`${styles.monthYearItem} ${selected ? styles.selected : ''} ${
                disabled ? styles.disabled : ''
              } ${isCurrentYear ? styles.today : ''}`}
              onClick={() => !disabled && handleYearSelect(year)}
              disabled={disabled}
            >
              {year}
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <div className={styles.calendar}>
      {/* Header */}
      <div className={styles.header}>
        <button
          type="button"
          className={styles.navButton}
          onClick={previousMonth}
          aria-label={viewMode === 'year' ? 'Previous years' : viewMode === 'month' ? 'Previous year' : 'Previous month'}
        >
          <IconsColor
            iconName="chevronLeft"
            width="20px"
            height="20px"
            fillColor="none"
            strokeColor="currentColor"
          />
        </button>

        <button
          type="button"
          className={styles.monthYearButton}
          onClick={handleHeaderClick}
          aria-label="Select month or year"
        >
          <div className={styles.monthYear}>
            {viewMode === 'year' 
              ? `${yearRange.start} - ${yearRange.end}`
              : viewMode === 'month'
              ? currentYear
              : `${monthNames[currentMonthIndex]} ${currentYear}`
            }
          </div>
        </button>

        <button
          type="button"
          className={styles.navButton}
          onClick={nextMonth}
          aria-label={viewMode === 'year' ? 'Next years' : viewMode === 'month' ? 'Next year' : 'Next month'}
        >
          <IconsColor
            iconName="chevronRight"
            width="20px"
            height="20px"
            fillColor="none"
            strokeColor="currentColor"
          />
        </button>
      </div>

      {/* Month Picker View */}
      {viewMode === 'month' && renderMonthPicker()}

      {/* Year Picker View */}
      {viewMode === 'year' && renderYearPicker()}

      {/* Calendar View */}
      {viewMode === 'calendar' && (
        <>
          {/* Day names */}
          <div className={styles.dayNames}>
            {dayNames.map((day) => (
              <div key={day} className={styles.dayName}>
                {day}
              </div>
            ))}
          </div>

          {/* Days grid */}
          <div className={styles.daysGrid}>
            {days.map((day, index) => {
              if (day === null) {
                return <div key={`empty-${index}`} className={styles.emptyDay} />;
              }

              const year = currentMonth.getFullYear();
              const month = currentMonth.getMonth();
              const disabled = isDateDisabled(year, month, day);
              const selected = isDateSelected(year, month, day);
              const today = isToday(year, month, day);

              return (
                <button
                  key={`day-${day}`}
                  type="button"
                  className={`${styles.day} ${selected ? styles.selected : ''} ${
                    disabled ? styles.disabled : ''
                  } ${today ? styles.today : ''}`}
                  onClick={() => handleDayClick(day)}
                  disabled={disabled}
                >
                  {day}
                </button>
              );
            })}
          </div>
        </>
      )}

      {/* Footer */}
      {onClose && (
        <div className={styles.footer}>
          <button type="button" className={styles.cancelButton} onClick={onClose}>
            Cancel
          </button>
        </div>
      )}
    </div>
  );
};

export default Calendar;

